import { useMemo, useState } from 'react';
import { Bookmark, Copy, Download, Plus, Trash2 } from 'lucide-react';
import { MACRO_META, MEAL_GROUPS, todayKey, uid } from './state';
import {
  buildBarcodeIndex, copyDayInto, editTemplate, entriesToTemplateItems, entryAmount, entryMacros,
  getFrequentFoods, getGroupFoodScores, getRecentFoods, groupEntries, isMassUnit, logFoodInto, logItemsInto,
  recentMealDays, setEntryAmount, summarizeEntries,
} from './food';
import { addDays, dayLabel } from './stats/statsData';
import { FONT_MONO } from './theme';
import { Button, Card, NumberInput } from './ui';
import { MacroRing } from './meals/MacroRing';
import { MealTemplateSheet } from './meals/MealTemplateSheet';
import { SupplementRow } from './meals/SupplementRow';
import { WeightCheckIn } from './meals/WeightCheckIn';
import { DailyNoteCard } from './meals/DailyNoteCard';
import { QuickAddSheet } from './meals/QuickAddSheet';
import { BarcodeScanner } from './meals/BarcodeScanner';
import { PhotoFoodSheet } from './meals/PhotoFoodSheet';
import { FoodEstimateSheet } from './meals/FoodEstimateSheet';
import { DayPicker } from './meals/DayPicker';
import { DayStatusChip } from './meals/DayStatusChip';
import { CopyMealSheet } from './meals/CopyMealSheet';
import { nextLogStatus, withLogStatus } from './meals/logStatus';
import { dayStatus } from './logic/energy';

/* ────────────────────────────────────────────────────────────────────────── */
/*  Meals tab                                                                 */
/* ────────────────────────────────────────────────────────────────────────── */

/* `date` (YYYY-MM-DD, null = today) lives in the root so Stats can open a
 * past day here; onDateChange gets null back for today. */
export function MealsTab({ t, state, setState, token, isDark = true, date = null, onDateChange }) {
  const [activeGroup, setActiveGroup] = useState(null); // meal group we're adding to
  const [scanOpen, setScanOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);
  const [estimateOpen, setEstimateOpen] = useState(false);
  const [estimateSeed, setEstimateSeed] = useState('');
  const [templateGroup, setTemplateGroup] = useState(null); // meal group whose templates are open
  const [copyFrom, setCopyFrom] = useState(null); // { group } or { wholeDay: true } while picking a day to copy
  const [pendingCode, setPendingCode] = useState(''); // unknown barcode handed to the label flow

  const today = todayKey();
  const key = date || today;
  const setDay = (k) => { if (onDateChange) onDateChange(k === today ? null : k); };
  // 'today' / 'yesterday' / 'Thu 15 Oct', for copy labels.
  const nameOf = (k) => (k === today ? 'today' : k === addDays(today, -1) ? 'yesterday' : k === addDays(today, 1) ? 'tomorrow' : dayLabel(k));
  const prevKey = addDays(key, -1);
  const prevEntries = state.nutrition[prevKey] || [];
  /* Time an entry was logged, only for today: a back-filled day gets none
   * rather than a wrong one. One call per user action, so all items of a
   * template or copy share it. */
  const stamp = () => (key === todayKey() ? Date.now() : undefined);
  const entries = state.nutrition[key] || [];
  const foods = state.foods || [];
  const recents = useMemo(() => getRecentFoods(state.nutrition), [state.nutrition]);
  const templates = state.mealTemplates || [];
  // Habit data is scoped to the group being added to, so breakfast history
  // never ranks anything in dinner (and vice versa).
  const frequent = useMemo(() => getFrequentFoods(state.nutrition, activeGroup), [state.nutrition, activeGroup]);
  const freqScores = useMemo(() => getGroupFoodScores(state.nutrition, activeGroup), [state.nutrition, activeGroup]);
  const targets = {
    kcal: state.profile.calorieTarget || 0,
    protein: state.profile.macros?.protein || 0,
    carbs: state.profile.macros?.carbs || 0,
    fats: state.profile.macros?.fats || 0,
  };

  const totals = entries.reduce((acc, e) => {
    const tt = entryMacros(e);
    return { kcal: acc.kcal + tt.kcal, protein: acc.protein + tt.protein, carbs: acc.carbs + tt.carbs, fats: acc.fats + tt.fats };
  }, { kcal: 0, protein: 0, carbs: 0, fats: 0 });
  const dailyNote = state.dailyNotes?.[key] || {};
  const markedStatus = state.logStatus?.[key];
  const autoStatus = dayStatus({ nutrition: state.nutrition, profile: state.profile }, key);
  const cycleStatus = () => setState(prev => withLogStatus(prev, key, nextLogStatus(prev.logStatus?.[key], autoStatus)));
  const clearStatus = () => setState(prev => withLogStatus(prev, key, null));

  // Returns the new entry's id so QuickAddSheet can undo exactly that add.
  const logFood = (food) => {
    if (!(food.name || '').trim()) return null;
    const id = uid('meal');
    const at = stamp();
    setState(prev => logFoodInto(prev, food, key, activeGroup, { id, at }));
    return id;
  };
  const barcodeIndex = useMemo(
    () => (scanOpen ? buildBarcodeIndex(state.nutrition, state.foods) : null),
    [scanOpen, state.nutrition, state.foods],
  );

  /* Templates. Applying one replays its items through the same fold as a
   * manual add, so the result is ordinary log entries: editable amounts,
   * removable individually, and counted in the habit ranking like anything
   * else you ate. */
  const saveTemplate = (group, name) => setState(prev => {
    const items = entriesToTemplateItems(groupEntries(prev.nutrition[key], group));
    if (!items.length) return prev;
    const tpl = { id: uid('tpl'), name, group, items, createdAt: new Date().toISOString() };
    return { ...prev, mealTemplates: [...(prev.mealTemplates || []), tpl] };
  });

  const applyTemplate = (tpl, group, scale = 1) => {
    const at = stamp();
    setState(prev => logItemsInto(prev, tpl.items, key, group, { at, scale }));
  };

  const updateTemplate = (id, patch) => setState(prev => editTemplate(prev, id, patch, new Date().toISOString()));

  /* Copies are the template fold over the source entries: new ids, the same
   * amounts, and the foods refreshed in the library like any add. */
  const copyGroup = (fromKey, group) => {
    const at = stamp();
    setState(prev => logItemsInto(
      prev, entriesToTemplateItems(groupEntries(prev.nutrition[fromKey], group)), key, group, { at },
    ));
  };

  const copyDay = (fromKey, mode) => {
    const at = stamp();
    setState(prev => copyDayInto(prev, fromKey, key, { mode, at }));
  };

  const deleteTemplate = (id) => setState(prev => ({
    ...prev, mealTemplates: (prev.mealTemplates || []).filter(x => x.id !== id),
  }));

  const deleteFood = (id) => setState(prev => ({ ...prev, foods: (prev.foods || []).filter(f => f.id !== id) }));

  const toggleFavorite = (id) => setState(prev => ({
    ...prev, foods: (prev.foods || []).map(f => f.id === id ? { ...f, favorite: !f.favorite } : f),
  }));

  const setAmount = (entryId, amount) => setState(prev => setEntryAmount(prev, key, entryId, amount));

  const removeEntry = (entryId) => setState(prev => {
    const day = (prev.nutrition[key] || []).filter(e => e.id !== entryId);
    const nutrition = { ...prev.nutrition };
    if (day.length) nutrition[key] = day; else delete nutrition[key];
    return { ...prev, nutrition };
  });

  const updateDailyNote = (field, value) => setState(prev => {
    const current = prev.dailyNotes?.[key] || {};
    const nextNote = { ...current, [field]: value };
    const dailyNotes = { ...(prev.dailyNotes || {}) };
    if (!String(nextNote.food || '').trim() && !String(nextNote.goal || '').trim()) delete dailyNotes[key];
    else dailyNotes[key] = nextNote;
    return { ...prev, dailyNotes };
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <DayPicker t={t} isDark={isDark} value={key} today={today} onChange={setDay} />

      {/* Daily totals vs targets — macro rings */}
      <Card t={t} padding={20}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 16 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.text }}>Daily totals</div>
            <div style={{ fontSize: 11, color: t.textFaint, marginTop: 2 }}>{entries.length} item{entries.length === 1 ? '' : 's'} logged</div>
          </div>
          <DayStatusChip t={t} isDark={isDark} marked={markedStatus} auto={autoStatus}
            onCycle={cycleStatus} onClear={clearStatus} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, justifyItems: 'center' }}>
          {MACRO_META.map(m => (
            <MacroRing key={m.key} t={t} label={m.label} value={totals[m.key]} target={targets[m.key]} unit={m.unit} color={m.color} />
          ))}
        </div>
      </Card>

      <WeightCheckIn t={t} state={state} setState={setState} dayKey={key} />

      <SupplementRow t={t} state={state} setState={setState} dayKey={key} />

      <DailyNoteCard t={t} note={dailyNote} onChange={updateDailyNote} />

      {/* Meal groups */}
      {MEAL_GROUPS.map(g => {
        const gEntries = groupEntries(entries, g.id);
        const gKcal = gEntries.reduce((s, e) => s + entryMacros(e).kcal, 0);
        const prev = gEntries.length ? null : summarizeEntries(groupEntries(prevEntries, g.id));
        return (
          <Card t={t} padding={0} key={g.id}>
            <div style={{
              padding: '6px 8px 6px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              borderBottom: gEntries.length ? `1px solid ${t.divider}` : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: t.text }}>{g.label}</span>
                {gEntries.length > 0 && (
                  <span style={{ fontSize: 11, color: t.textFaint, fontFamily: FONT_MONO }}>{Math.round(gKcal)} kcal</span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <Button t={t} size="sm" variant="ghost" onClick={() => setCopyFrom({ group: g.id })}
                  title={`Copy ${g.label.toLowerCase()} from another day`} aria-label={`Copy ${g.label.toLowerCase()} from another day`} style={ICON_BTN}>
                  <Copy size={15} />
                </Button>
                <Button t={t} size="sm" variant="ghost" onClick={() => setTemplateGroup(g.id)}
                  title={`${g.label} templates`} aria-label={`${g.label} templates`} style={ICON_BTN}>
                  <Bookmark size={15} />
                </Button>
                <Button t={t} size="sm" variant="ghost" onClick={() => setActiveGroup(g.id)} style={{ minHeight: 40 }}>
                  <Plus size={14} /> Add
                </Button>
              </div>
            </div>
            {gEntries.length === 0 ? (
              prev.count > 0 ? (
                <div style={{ padding: '0 12px 12px' }}>
                  <button type="button" onClick={() => copyGroup(prevKey, g.id)} style={{
                    width: '100%', minHeight: 44, padding: '8px 12px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                    background: t.accentSoft, border: `1px dashed ${t.accentBorder}`, color: t.accent,
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <Copy size={15} style={{ flexShrink: 0 }} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 700 }}>
                        Same as {nameOf(prevKey)}
                        <span style={{ fontFamily: FONT_MONO, fontWeight: 600 }}> · {Math.round(prev.kcal)} kcal {Math.round(prev.protein)} P</span>
                      </span>
                      <span style={{ display: 'block', fontSize: 11, color: t.textMuted, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {prev.names.join(', ')}{prev.count > prev.names.length ? ` +${prev.count - prev.names.length}` : ''}
                      </span>
                    </span>
                  </button>
                </div>
              ) : (
                <div style={{ padding: '0 16px 12px', fontSize: 12, color: t.textFaint }}>No items yet.</div>
              )
            ) : gEntries.map((e, i) => {
              const tt = entryMacros(e);
              const unit = e.unit || 'serving';
              const amt = entryAmount(e);
              return (
                <div key={e.id} title={Number.isFinite(e.at) ? `Logged at ${timeOf(e.at)}` : undefined}
                  style={{ padding: '12px 16px', borderTop: i > 0 ? `1px solid ${t.divider}` : 'none', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: t.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</div>
                    <div style={{ fontSize: 11, color: t.textFaint, fontFamily: FONT_MONO, marginTop: 2 }}>
                      {Math.round(tt.kcal)} kcal · {Math.round(tt.protein)}P {Math.round(tt.carbs)}C {Math.round(tt.fats)}F
                    </div>
                  </div>
                  <div style={{ width: 82, flexShrink: 0 }}>
                    <NumberInput t={t} value={amt} onChange={v => setAmount(e.id, v)} step={isMassUnit(unit) ? 10 : 0.5} min={0} suffix={isMassUnit(unit) ? unit : '×'} />
                  </div>
                  <button onClick={() => removeEntry(e.id)} title="Remove" style={{ flexShrink: 0, background: 'transparent', border: 'none', cursor: 'pointer', color: t.textFaint, display: 'flex', padding: 4 }}>
                    <Trash2 size={15} />
                  </button>
                </div>
              );
            })}
          </Card>
        );
      })}

      {/* Whole-day copy + JSON export */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Button t={t} size="sm" onClick={() => setCopyFrom({ wholeDay: true })} style={{ minHeight: 40 }}>
          <Copy size={14} /> Copy whole day from…
        </Button>
        <Button t={t} size="sm" variant="ghost" style={{ minHeight: 40 }} onClick={() => {
          const payload = {
            exported_at: new Date().toISOString(),
            date: key,
            targets,
            totals: {
              kcal: Math.round(totals.kcal), protein: Math.round(totals.protein),
              carbs: Math.round(totals.carbs), fats: Math.round(totals.fats),
            },
            daily_log: {
              meal_context: dailyNote.food || '',
              missed_target_reason: dailyNote.goal || '',
            },
            meals: MEAL_GROUPS.map(g => ({
              group: g.id,
              label: g.label,
              items: groupEntries(entries, g.id).map(e => {
                const m = entryMacros(e);
                const unit = e.unit || 'serving';
                return {
                  name: e.name,
                  brand: e.brand || undefined,
                  amount: entryAmount(e),
                  unit,
                  kcal: Math.round(m.kcal), protein: Math.round(m.protein),
                  carbs: Math.round(m.carbs), fats: Math.round(m.fats),
                  source: e.source || undefined,
                };
              }),
            })),
          };
          const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `meals-${key}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }}>
          <Download size={13} /> Export JSON
        </Button>
      </div>

      {activeGroup && !scanOpen && !photoOpen && !labelOpen && !estimateOpen && (
        <QuickAddSheet
          t={t} token={token}
          groupLabel={MEAL_GROUPS.find(g => g.id === activeGroup)?.label || 'meal'}
          foods={foods} recents={recents} frequent={frequent} freqScores={freqScores}
          onLog={logFood} onUndo={removeEntry} onDeleteFood={deleteFood} onToggleFavorite={toggleFavorite}
          onScan={() => setScanOpen(true)}
          onPhoto={() => setPhotoOpen(true)}
          onLabel={() => setLabelOpen(true)}
          onEstimate={description => { setEstimateSeed(description); setEstimateOpen(true); }}
          onClose={() => setActiveGroup(null)} />
      )}

      {scanOpen && (
        <BarcodeScanner t={t} token={token} index={barcodeIndex} onLog={logFood}
          // Straight to the label flow, keeping activeGroup so the food
          // lands in the meal Mo was adding to.
          onReadLabel={code => { setScanOpen(false); setPendingCode(code); setLabelOpen(true); }}
          onClose={() => { setScanOpen(false); setActiveGroup(null); }} />
      )}

      {photoOpen && (
        <PhotoFoodSheet t={t} token={token} mode="meal"
          groupLabel={MEAL_GROUPS.find(g => g.id === activeGroup)?.label || 'meal'}
          onLog={logFood}
          onClose={() => { setPhotoOpen(false); setActiveGroup(null); }} />
      )}

      {labelOpen && (
        <PhotoFoodSheet t={t} token={token} mode="label" pendingCode={pendingCode}
          groupLabel={MEAL_GROUPS.find(g => g.id === activeGroup)?.label || 'meal'}
          onLog={logFood}
          onClose={() => { setLabelOpen(false); setPendingCode(''); setActiveGroup(null); }} />
      )}

      {templateGroup && (
        <MealTemplateSheet
          t={t} group={templateGroup}
          groupLabel={MEAL_GROUPS.find(g => g.id === templateGroup)?.label || 'meal'}
          templates={templates} dayText={nameOf(key)}
          currentEntries={groupEntries(entries, templateGroup)}
          onApply={(tpl, scale) => applyTemplate(tpl, templateGroup, scale)}
          onSave={name => saveTemplate(templateGroup, name)}
          onUpdate={updateTemplate}
          onDelete={deleteTemplate}
          onClose={() => setTemplateGroup(null)} />
      )}

      {copyFrom && (() => {
        const label = copyFrom.group ? MEAL_GROUPS.find(g => g.id === copyFrom.group)?.label || 'meal' : null;
        return (
          <CopyMealSheet t={t} today={today}
            title={label ? `Copy ${label.toLowerCase()}` : 'Copy a whole day'}
            wholeDay={!copyFrom.group}
            targetText={nameOf(key)} targetCount={entries.length}
            days={recentMealDays(state.nutrition, { group: copyFrom.group || null, excludeKey: key, maxKey: addDays(today, 1) })}
            onCopy={(fromKey, mode) => (copyFrom.group ? copyGroup(fromKey, copyFrom.group) : copyDay(fromKey, mode))}
            onClose={() => setCopyFrom(null)} />
        );
      })()}

      {estimateOpen && (
        <FoodEstimateSheet t={t} token={token}
          groupLabel={MEAL_GROUPS.find(g => g.id === activeGroup)?.label || 'meal'}
          initialDescription={estimateSeed}
          onLog={logFood}
          onClose={() => { setEstimateOpen(false); setActiveGroup(null); }} />
      )}
    </div>
  );
}

const ICON_BTN = { width: 40, height: 40, padding: 0, justifyContent: 'center' };

function timeOf(ms) {
  return new Date(ms).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
